import { useRef, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../tailwind/dialog";
import { Button } from "../tailwind/button";
import {
  ImagePlus,
  Loader2,
} from "lucide-react";
import { useProfileForm } from "../../hooks/useProfileForm";
import { MAX_LENGTHS } from "../../constants/profileConstants";
import { fetchCategoriesRequest } from "../../store/whatsappCategoriesSlice";
import ProfilePreviewSection from "./ProfilePreviewSection";
import FormField from "./FormField";
import CharacterCounter from "./CharacterCounter";

export default function ManageProfileDialog({
  isOpen,
  onClose,
  phoneNumber,
  phoneNumberId,
  onSave,
  initialData = {},
}) {
  const dispatch = useDispatch();
  const companyInfo = useSelector(state => state.companyInfo?.info || {});
  const companyId = useSelector(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id);
  const { phoneProfile, phoneProfileLoading, phoneProfileLoaded } = useSelector(state => state.whatsappProfile);
  const { categories, loading: categoriesLoading } = useSelector(state => state.whatsappCategories);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCategoriesRequest());
    }
  }, [isOpen, dispatch]);

  const normalizeInitialProfileData = (source = {}) => ({
    displayName: source.displayName ?? '',
    displayNamePending: source.displayNamePending ?? null,
    category: source.category ?? '',
    description: source.description ?? '',
    address: source.address ?? '',
    email: source.email ?? '',
    website1: source.website1 ?? '',
    website2: source.website2 ?? '',
    profileImage: source.profileImage ?? null,
  });

  const mergedInitialData = useMemo(() => {
    if (phoneProfileLoaded) {
      const websites = Array.isArray(phoneProfile?.websites) ? phoneProfile.websites : [];

      return normalizeInitialProfileData({
        ...phoneProfile,
        website1: websites[0] ?? '',
        website2: websites[1] ?? '',
        profileImage: phoneProfile?.imageUrl ?? null,
      });
    }

    return normalizeInitialProfileData(initialData);
  }, [phoneProfile, initialData, phoneProfileLoaded]);

  const {
    formData,
    errors,
    isSubmitting,
    success,
    updateField,
    handleProfileImageChange,
    removeProfileImage,
    submitForm,
  } = useProfileForm(mergedInitialData, isOpen);

  useEffect(() => {
    if (isOpen && success) {
      onSave?.();
      onClose();
    }
  }, [isOpen, success, onClose, onSave]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateField(name, value);
  };

  const handleProfilePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await handleProfileImageChange(file);
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleRemoveProfilePhoto = () => {
    removeProfileImage();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!companyId || !phoneNumberId) {
      return;
    }

    await submitForm(companyId, phoneNumberId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="overflow-hidden border-0 bg-transparent p-0 shadow-none sm:max-w-[1200px]">
        <div className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-700 px-8 py-5 shrink-0">
            <div>
              <DialogTitle className="text-xl font-semibold text-slate-900 dark:text-gray-100">
                Manage profile
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                Update the business profile for {phoneNumber}. This will be visible on WhatsApp.
              </DialogDescription>
            </div>
          </div>

          {phoneProfileLoading && (
            <div className="flex items-center justify-center py-12 border-b border-slate-200 dark:border-gray-700">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
                <p className="text-sm text-slate-600 dark:text-gray-400">Loading profile data...</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Column - Form */}
            <div className="flex-1 overflow-y-auto px-8 py-6 border-r border-slate-200 dark:border-gray-700 min-w-0">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={handleProfilePhotoChange}
              />

              <div className="space-y-6 max-w-2xl">
                {/* Error Message */}
                {errors.submit && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                    {errors.submit}
                  </div>
                )}

                {/* Profile Picture */}
                <div className="space-y-1">
                  <label className="text-[15px] font-semibold text-slate-900 dark:text-gray-100">
                    Profile picture
                  </label>
                  <p className="text-[13px] text-slate-600 dark:text-gray-400">
                    This will be visible on your business profile
                  </p>
                  <div className="flex items-center gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 px-3 text-xs font-medium border-slate-300 dark:border-gray-600 rounded text-slate-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <ImagePlus className="h-3.5 w-3.5 mr-1.5" />
                      Choose file
                    </Button>
                    {formData.profileImage && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-8 px-3 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        onClick={handleRemoveProfilePhoto}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>

                <hr className="border-slate-100 dark:border-gray-700" />

                {/* Business Information */}
                <div className="pt-1">
                  <div className="mb-4">
                    <h4 className="text-[15px] font-semibold text-slate-900 dark:text-gray-100">
                      Business information
                    </h4>
                    <p className="text-[13px] text-slate-600 dark:text-gray-400 mt-0.5">
                      Add some details about your business
                    </p>
                  </div>

                  <div className="space-y-4">
                    {/* Display Name */}
                    <FormField label="Display Name" error={errors.displayName}>
                      <input
                        type="text"
                        name="displayName"
                        value={formData.displayName}
                        onChange={handleInputChange}
                        disabled={!!formData.displayNamePending}
                        className="w-full rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter your business name"
                      />
                      <CharacterCounter
                        current={formData.displayName.length}
                        max={MAX_LENGTHS.displayName}
                      />
                      {formData.displayNamePending && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Your display name change is pending approval and can be re-changed after approval
                        </p>
                      )}
                    </FormField>

                    {/* Category */}
                    <FormField label="Category">
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        disabled={categoriesLoading}
                        className="w-full rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {categoriesLoading ? (
                          <option>Loading categories...</option>
                        ) : categories.length > 0 ? (
                          categories.map(category => (
                            <option key={category.value} value={category.value}>{category.label}</option>
                          ))
                        ) : (
                          <option>No categories available</option>
                        )}
                      </select>
                    </FormField>

                    {/* Description */}
                    <FormField label="Description" optional error={errors.description}>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="w-full resize-none rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[92px]"
                        placeholder="Tell customers about your business"
                      />
                      <CharacterCounter
                        current={formData.description.length}
                        max={MAX_LENGTHS.description}
                      />
                    </FormField>

                    {/* Row: Address and Email */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Address */}
                      <FormField label="Address" optional error={errors.address}>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter business address"
                        />
                        <CharacterCounter
                          current={formData.address.length}
                          max={MAX_LENGTHS.address}
                        />
                      </FormField>

                      {/* Email */}
                      <FormField label="Email" optional error={errors.email}>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter business email address"
                        />
                        <CharacterCounter
                          current={formData.email.length}
                          max={MAX_LENGTHS.email}
                        />
                      </FormField>
                    </div>

                    {/* Row: Websites */}
                    <FormField label="Website" optional>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div>
                          <input
                            type="url"
                            name="website1"
                            value={formData.website1}
                            onChange={handleInputChange}
                            className="w-full rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            placeholder="https://www.example.com"
                          />
                          <CharacterCounter
                            current={formData.website1.length}
                            max={MAX_LENGTHS.website}
                          />
                          {errors.website1 && <p className="text-[11px] text-red-500 mt-1">{errors.website1}</p>}
                        </div>
                        <div>
                          <input
                            type="url"
                            name="website2"
                            value={formData.website2}
                            onChange={handleInputChange}
                            className="w-full rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            placeholder="https://www.example.com"
                          />
                          <CharacterCounter
                            current={formData.website2.length}
                            max={MAX_LENGTHS.website}
                          />
                          {errors.website2 && <p className="text-[11px] text-red-500 mt-1">{errors.website2}</p>}
                        </div>
                      </div>
                    </FormField>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Preview */}
            <div className="w-120 flex-shrink-0 overflow-y-auto border-l border-slate-200 dark:border-gray-700">
              <ProfilePreviewSection
                profileData={{
                  ...formData,
                  profileImage: formData.profileImagePreview,
                  phone: phoneNumber,
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 px-8 py-4 sm:justify-between sm:space-x-0 rounded-b-xl shrink-0">
            <Button
              type="button"
              variant="outline"
              className="rounded border-slate-300 dark:border-gray-600 h-9 px-4 font-medium hover:bg-slate-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="rounded h-9 px-6 bg-[#0068ff] text-white font-medium hover:bg-[#0052cc] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
